import { ReactNode, useRef, useState, useEffect } from "react";
import { ArrowDownIcon, ArrowUpIcon, ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { colorCodes } from "../../board/components/TaskDetail/ColorPicker";

type DataBucketRowProps = {
  className?: string;
  bodyClassName?: string;
  isActive?: boolean;
  onClick?: () => void;
  footer?: ReactNode;

  index: number;
  bucketId: string;
  bucketValue: string;
  bucketColor?: string;
  bucketWeight?: string;
  weightSummary?: string;
  placeholder?: string;

  isLoading?: boolean;
  isReordering?: boolean;
  isReorderingUp?: boolean;
  isReorderingDown?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;

  onInputChange: (value: string) => void;
  onColorChange?: (color: string) => void;
  onWeightChange?: (value: string) => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onWeightBlur?: () => void;
  onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onWeightKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onReorderUp?: () => void;
  onReorderDown?: () => void;
  onDelete?: () => void;

  t: (key: string) => string;
};

/** Auto-select next available color that isn't used by existing buckets */
export const getNextAvailableColor = (usedColors: string[]): string => {
  const available = colorCodes.find((cc) => !usedColors.includes(cc.color));
  if (available) return available.color;
  // All colors used — cycle from beginning
  return colorCodes[usedColors.length % colorCodes.length].color;
};

/** Compact inline color dot with dropdown list picker */
export const ColorDot = ({
  color,
  onChange,
  disabled,
}: {
  color?: string;
  onChange?: (color: string) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const currentColor = colorCodes.find((cc) => cc.color === color);
  const colorCode = currentColor?.code;

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        disabled={disabled}
        className="flex items-center gap-0.5 rounded-md px-1 py-1 hover:bg-gray-100 transition disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Pick color"
      >
        <span
          className="block w-3.5 h-3.5 rounded-full shrink-0"
          style={{ backgroundColor: colorCode || "#d1d5db" }}
        />
        <ChevronDownIcon className="w-3 h-3 text-gray-400" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-40 max-h-52 overflow-y-auto bg-white rounded-lg border border-gray-200 shadow-lg py-1">
          {colorCodes.map((option) => {
            const isSelected = option.color === color;
            return (
              <button
                key={option.name}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(option.color);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
                  isSelected ? "bg-indigo-50 text-gray-900" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span
                  className="block w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: option.code }}
                />
                <span className="truncate">{option.name}</span>
                {isSelected && (
                  <svg
                    className="w-3.5 h-3.5 ml-auto text-indigo-600 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DataBucketRow = ({
  className = "",
  bodyClassName = "items-center",
  isActive = false,
  onClick,
  footer,
  bucketId: _bucketId,
  bucketValue,
  bucketColor,
  bucketWeight = "",
  weightSummary,
  placeholder = "Name",
  isLoading = false,
  isReordering = false,
  isReorderingUp = false,
  isReorderingDown = false,
  canMoveUp = true,
  canMoveDown = true,
  // Event handlers
  onInputChange,
  onColorChange,
  onWeightChange,
  onInputFocus,
  onInputBlur,
  onWeightBlur,
  onInputKeyDown,
  onWeightKeyDown,
  onReorderUp,
  onReorderDown,
  onDelete,
  // Translation
  t,
}: DataBucketRowProps) => {
  return (
    <div
      onClick={onClick}
      className={`rounded-lg border bg-white shadow-sm transition ${
        isActive
          ? "border-indigo-500 ring-4 ring-indigo-500/10"
          : "border-gray-200 hover:border-gray-300"
      } ${className}`}
    >
      <div className={`flex gap-2 ${bodyClassName}`}>
        <div className="flex items-center gap-1 w-full">
          <ColorDot
            color={bucketColor}
            onChange={onColorChange}
            disabled={isLoading || isReordering}
          />
          <input
            value={bucketValue}
            placeholder={t(placeholder)}
            disabled={isLoading || isReordering}
            onChange={(event) => onInputChange(event.target.value)}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            onKeyDown={onInputKeyDown}
            className="flex-1 px-2.5 py-1 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
          {onWeightChange ? (
            <div className="flex flex-col items-end gap-1">
              <input
                type="number"
                step="0.01"
                min="0"
                value={bucketWeight}
                placeholder={t("Weight")}
                disabled={isLoading || isReordering}
                onChange={(event) => onWeightChange(event.target.value)}
                onBlur={onWeightBlur}
                onKeyDown={onWeightKeyDown}
                className="w-20 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
              />
              {weightSummary ? (
                <span className="text-[10px] text-gray-500">{weightSummary}</span>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReorderUp?.();
              }}
              disabled={!canMoveUp || isLoading || isReordering}
              className="rounded-full p-1 text-gray-400 transition disabled:cursor-not-allowed disabled:text-gray-200"
              aria-label={t("Up")}
            >
              {isReorderingUp ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-600" />
              ) : (
                <ArrowUpIcon className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReorderDown?.();
              }}
              disabled={!canMoveDown || isLoading || isReordering}
              className="rounded-full p-1 text-gray-400 transition disabled:cursor-not-allowed disabled:text-gray-200"
              aria-label={t("Down")}
            >
              {isReorderingDown ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-600" />
              ) : (
                <ArrowDownIcon className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
              disabled={isLoading || isReordering}
              className="rounded-full p-1 text-gray-400 transition disabled:cursor-not-allowed disabled:text-gray-200"
              aria-label={t("Delete")}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {footer ? <div className="mt-2">{footer}</div> : null}
    </div>
  );
};

export default DataBucketRow;
